import {captureException} from '@sentry/browser';
import i18n from 'i18next';
import {showNotification} from 'platform/components';

import {isNotNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import type {RowData} from '../types/RowData';

/**
 * PURE accessor for a column's data on a row.
 *
 * Returns the column entry (`{value, style, id}`) when present, otherwise `null`.
 * Has NO side effects — missing-column notification/Sentry reporting is the
 * caller's responsibility (see `notifyMissingColumnData`). The factory shape
 * `getColumnData(row, gridCode)(column, options)` is preserved for call sites
 * that compose it with the side-effect reporter.
 */
export const getColumnData =
  (row: RowData, _gridCode: string) =>
  <T = unknown, S = unknown>(column: string, _options?: {isOptional?: boolean}) => {
    const columnData = isArray(row) ? row?.[0]?.[column] : row?.[column];

    // success response
    if (isNotNil(columnData)) {
      return columnData as {value: T; style: S; id: string};
    }

    return null;
  };

/**
 * Side effect for a missing non-optional column: shows a blocking error
 * notification and captures a Sentry exception. Call sites invoke this ONLY
 * when the pure accessor returned `null` and the lookup was not optional.
 */
export const notifyMissingColumnData = (gridCode: string, column: string) => {
  // production error handling
  showNotification.error(
    i18n.t('page.datagrid.notifications.columnDataIsNull', {columnKey: column}),
    {autoClose: false}
  );

  // Always capture in Sentry — no process.env needed for browser builds
  captureException(new Error(`${gridCode} Column data for ${column} column is null or undefined`));
};

/**
 * Reporting `getColumnData` accessor used by the action callbacks: combines the
 * pure {@link getColumnData} lookup with the {@link notifyMissingColumnData}
 * side effect. When the column is missing and the lookup is NOT optional, it
 * fires the notification + Sentry capture (identical to the previous behavior).
 */
export const getColumnDataWithReporting =
  (row: RowData, gridCode: string) =>
  <T = unknown, S = unknown>(column: string, options?: {isOptional?: boolean}) => {
    const columnData = getColumnData(row, gridCode)<T, S>(column, options);

    if (columnData === null && !options?.isOptional) {
      notifyMissingColumnData(gridCode, column);
    }

    return columnData;
  };
